import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL, ENDPOINTS } from '../constants/api';
import { notification } from 'antd';
import AutoCompleteInput from './AutoCompleteInput';
import * as XLSX from 'xlsx';
import { filterRow, submitRow } from '../utils/ktcUtils';

const DataEntryTable = ({
  data,
  reportHeader,
  reportFooter,
  link,
  token,
  executionTime,
  setData,
}) => {
  const [columns, setColumns] = useState([]);
  const [data2, setData2] = useState([]);
  // console.log('link:', link);
  // Step 1: Define the state to track which rows have been processed
  const [processedRows, setProcessedRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showApiUpdateButton, setShowApiUpdateButton] = useState(false);
  // Initial state with the original data passed as a prop
  const [originalData, setOriginalData] = useState(data);
  const [filteredData, setFilteredData] = useState(data); // Filtered data to show based on search query
  const [failedData, setFailedData] = useState([]);
  const [message, setMessage] = useState(null);
  const updateApiValues = async () => {
    setLoading(true);

    const successfulRows = [];
    const failedRows = [];

    try {
      for (let i = 0; i < filteredData.length; i++) {
        const row = filteredData[i];
        const updatedRow = { ...row };
        const fetchPromises = [];
        let expression;

        for (const columnKey in row) {
          const parts = columnKey?.split('~');

          if (parts[2] === 'api_url' && parts.length >= 3) {
            const targetKey = parts[3];
            const targetKeyExp = parts[4];

            const originalUrl = row[columnKey];
            //expression = '5*6=7';
            // console.log('expression', expression);
            fetchPromises.push(
              axios
                .get(`${API_URL}/${ENDPOINTS.SYS}/proxy`, {
                  params: { url: originalUrl },
                  timeout: 5000,
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                })
                .then((response) => {
                  const newValue = response.data?.[targetKey];
                  // console.log(
                  //   'newValue:',
                  //   newValue,
                  //   'response.data:',
                  //   response.data
                  // );
                  if (newValue !== undefined) {
                    const targetColumn = Object.keys(row).find(
                      (k) => k?.split('~')[3] === targetKey
                    );

                    if (targetColumn) {
                      updatedRow[targetColumn] = newValue;
                    }
                  } else {
                    // ✅ If targetKey not found, treat as error
                    // console.log(
                    //   `Target key "${targetKey}" missing from response for ${originalUrl}`
                    // );
                    updatedRow[`error_${targetKey}`] = true;
                  }
                })
                .catch((err) => {
                  console.log(`Failed to fetch ${originalUrl}`, err.message);
                  updatedRow[`error_${targetKey}`] = true;
                })
            );
          }
        }

        await Promise.allSettled(fetchPromises);

        // ✅ After API fetches, calculate using your function
        // calculateExpression(
        //   expression, // you should define it before
        //   i, // current row index
        //   updatedRow, // current row
        //   filteredData, // original data or updatedData
        //   setFilteredData, // setter
        //   true // supportNegative maybe true (or your flag)
        // );
        const hasError = Object.keys(updatedRow).some((key) =>
          key.startsWith('error_')
        );

        if (hasError) {
          failedRows.push(updatedRow);
        } else {
          successfulRows.push(updatedRow);
        }
      }

      setFilteredData(successfulRows);
      setOriginalData(successfulRows);

      setFailedData(failedRows);
    } catch (error) {
      console.error('Unexpected error:', error);
    } finally {
      setLoading(false);
    }
  };

  // console.log('filteredData:', filteredData);

  // Create a ref for the input element
  const inputRef = useRef(null);

  useEffect(() => {
    // Fetch column definitions and data from the API
    const columns = Object.keys(data[0]);

    //columsn with dropdown
    const cols = columns.map((col) => {
      const parts = col?.split('~');
      const columnName = col;
      const header = parts[0]; // Display label
      const key = parts[1] || parts[0]; // Key used for row data
      const type = parts[2]; // 'editable', 'dropdown', 'hide'
      const inputId = parts[3]; // Used to fetch dropdown options
      const colIndex = parts[4];

      const isDropdown = type?.includes('dropdown');
      const isCheckbox = type?.includes('checkbox');
      const isEditable = type?.includes('editable') || type === 'api_url';
      const isSum = type?.includes('sum');

      const isHidden = type === 'hide';

      return {
        columnName,
        header,
        key,
        type,
        inputId,
        colIndex,
        isDropdown,
        isCheckbox,
        editable: isEditable,
        visible: !isHidden,
        dropdownOptions: isDropdown ? [] : null,
        sum: isSum,
      };
    });

    setColumns(cols);

    // check if has api_url column and show button to call update api url
    if (data?.length > 0) {
      const firstRow = data[0];
      const hasApiUrlColumn = Object.keys(firstRow).some(
        (key) => key?.split('~')[2] === 'api_url'
      );
      setShowApiUpdateButton(hasApiUrlColumn);
      // console.log('button shows: ', hasApiUrlColumn);
    } else {
      // console.log('button not shows');
    }

    const fetchAllDropdowns = async (cols) => {
      for (const col of cols) {
        if ((col.isDropdown || col.isCheckbox) && col.inputId) {
          // console.log('col:', col);
          const url = `${API_URL}/${ENDPOINTS.SYS}/get-dropdown/${col.inputId}`;

          try {
            const response = await axios.get(url, {
              headers: {
                Authorization: token ? `Bearer ${token}` : '',
              },
            });
            const options = response.data;
            // console.log('url:', url, 'response:', response.data);

            setColumns((prevColumns) =>
              prevColumns.map((c) =>
                c.key === col.key ? { ...c, dropdownOptions: options } : c
              )
            );
          } catch (err) {
            console.error(`Dropdown fetch failed for ${col.inputId}`, err);
          }
        }
      }
    };

    if (cols.length) {
      // console.log('length is true');
      fetchAllDropdowns(cols);
    } else {
      // console.log('length is false');
    }
  }, []);

  const [search, setSearch] = useState('');

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(e.target.value);

    // Filter data based on the search query
    const filtered = originalData.filter((row) =>
      Object.values(row).some((val) =>
        val?.toString().toLowerCase().includes(value.toLowerCase())
      )
    );
    setFilteredData(filtered);
  };

  const [loading, setLoading] = useState(false);

  const handleEdit = async (e, rowIndex2, colData, row, colIndex) => {
    // console.log('e.target:', e.target);
    try {
      const columnName = colData.columnName;
      // console.log(columnName, rowIndex2, colIndex, row, colData);

      const updatedData = [...data];

      var text = e.target.textContent;

      if (colData.type.includes('dropdown')) {
        text = e.target.value;
      }
      if (colData.type === 'autocomplete') {
        console.log('autocomplete:', e.target);
        text = e.target.value;
      }

      if (colData.type === 'input') {
        text = e.target.value;
      }

      if (colData.inputId === 'file') {
        text = e.target.files[0];
        console.log('Selected file:', text);
      }

      if (colData.inputId === 'multi-file') {
        text = Array.from(e.target.files);
        console.log('Selected file:', text);
      }

      // Find the index of the row in the original data using the row's content
      const rowIndex = updatedData.findIndex(
        (r) => JSON.stringify(r) === JSON.stringify(row)
      );

      if (colData.type === 'load-dropdown') {
        try {
          setLoading(true);
          const url = `${API_URL}/${ENDPOINTS.SYS}/load-dropdown/${colData.inputId}/${text}`;
          const response = await axios.get(url, {
            headers: {
              Authorization: token ? `Bearer ${token}` : '',
            },
          });
          const newOptions = response.data.results;
          // console.log('Loaded dropdown options:', newOptions);

          // 💡 Parse colIndex and convert to number
          const targetColIndex = parseInt(colData.colIndex, 10);

          // Use a unique key for dropdown options per column (e.g., columnName + "_options")
          const optionsKey = `${columns[targetColIndex].columnName}~options`;

          // Assign the new options to that row only
          updatedData[rowIndex][optionsKey] = newOptions;

          // Optionally clear previous value
          //updatedData[rowIndex][columns[targetColIndex].columnName] = '';

          //  setData([...updatedData]);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
        } finally {
          setLoading(false);
        }
      }

      //console.log('type:', colData.type);

      if (rowIndex === -1) {
        return;
      }

      updatedData[rowIndex][columnName] = text;
      console.log('value:', columnName, ':', text);
      console.log('row:', updatedData[rowIndex]);
      console.log('all rows:', updatedData);

      console.log(updatedData[rowIndex][columnName], ':', text);

      // Debug: Freeze row to detect mutations
      // Object.freeze(updatedData[rowIndex]);
      setFilteredData(updatedData);
      // console.log(
      //   'row data snapshot:',
      //   JSON.parse(JSON.stringify(updatedData[rowIndex])),
      //   'updatedData:',
      //   updatedData
      // );
      // console.log('column value snapshot:', updatedData[rowIndex][columnName]);
      const expression = columnName?.split('~')[3]?.split(',');
      const isSupportNegative = columnName?.split('~')[4] ?? '1';

      if (!expression) {
        return;
      }

      //console.log('expression:', expression);

      if (expression[0]) {
        calculateExpression(
          expression[0],
          rowIndex,
          row,
          updatedData,
          setData,
          isSupportNegative
        );
      }

      if (expression[1]) {
        calculateExpression(
          expression[1],
          rowIndex,
          row,
          updatedData,
          setData,
          isSupportNegative
        );
      }

      if (expression[2]) {
        calculateExpression(
          expression[2],
          rowIndex,
          row,
          updatedData,
          setData,
          isSupportNegative
        );
      }

      if (expression[0].includes('autotick')) handleSubmit(row, rowIndex);
    } catch (err) {
      alert(err);
    }
  };

  // console.log('filtered Data:', filteredData);
  function calculateExpression(
    expression,
    rowIndex,
    row,
    updatedData,
    setData,
    isSupportNegative
  ) {
    // Parse the string like "7-6=8"
    const parts = expression?.split('=');
    const leftSide = parts[0]; // "7-6"
    const rightSide = parts[1]; // "8" - column where result should be set
    if (!leftSide || !rightSide) {
      return;
    }
    // Create a regex that matches any of the delimiters: -, +, *, /
    const delimiterRegex = /[-+*/]/;

    // Check if the string contains any of the specified delimiters
    const match = leftSide.match(delimiterRegex);

    if (!match) {
      return;
    }

    // The first matched delimiter will be in the 'match' array
    const delimiter = match[0];
    // console.log('Found delimiter:', delimiter);

    // Split the string by the found delimiter and convert to integers
    const [col1, col2] = leftSide
      ?.split(delimiter) // Split by the found delimiter
      .map((index) => parseInt(index.trim())); // Convert to integers

    // console.log('col1:', col1);
    // console.log('col2:', col2);

    const col3 = rightSide;
    // Convert the object keys into an array
    const keys = Object.keys(row);

    // Get the values from the row data
    let value1 = row[keys[col1]];
    let value2 = row[keys[col2]];

    // console.log('keys:', keys);

    // console.log(`col1 - ${keys[col1]} Value at index ${col1} : ${value1}`);
    // console.log(`col2 - ${keys[col2]} Value at index ${col2} : ${value2}`);

    // Perform the subtraction
    var result = 0;

    if (isNaN(parseFloat(value1))) {
      notification.error({
        message: `${keys[col1]?.split('~')[0]} Value: '${value1}'  at index ${col1} is not a number`,
      });
    }
    if (isNaN(parseFloat(value2))) {
      notification.error({
        message: `${keys[col2]?.split('~')[0]} Value : '${value2}' at index ${col2}  is not a number`,
      });
    }
    if (delimiter === '-') {
      result = value1 - value2;
      if (result < 0 && isSupportNegative === '0') {
        notification.error({ message: 'The difference is negative.' });
        return;
      }
    } else if (delimiter === '+') {
      result = parseFloat(value1) + parseFloat(value2);
    } else if (delimiter === '*') {
      result = parseFloat(value1) * parseFloat(value2);
    } else if (delimiter === '/') {
      result = parseFloat(value1) / parseFloat(value2);
    }
    console.log(`${value1} ${delimiter} ${value2} = ${result}`);

    updatedData[rowIndex][keys[col3]] =
      value1 === '' || value2 === '' ? '' : result.toFixed(2);

    setData(updatedData);
  }

  const handleSubmit = async (row, rowIndex) => {
    const result = await submitRow(row, rowIndex, token);

    if (result.success) {
      setProcessedRows((prev) => [...prev, rowIndex]);

      const updatedData = originalData.filter(
        (dataRow) => JSON.stringify(dataRow) !== JSON.stringify(row)
      );
      setOriginalData(updatedData);

      const updatedFilteredData = filteredData.filter(
        (dataRow) => JSON.stringify(dataRow) !== JSON.stringify(row)
      );
      setFilteredData(updatedFilteredData);

      if (inputRef.current) {
        inputRef.current.focus();
      }
      setSearch('');
      notification.success({ message: result.message });
    } else {
      notification.error({ message: result.message });
    }
  };

  const renderCell = ({ col, row, rowIndex, colIndex, handleEdit }) => {
    const value = row[col.columnName] || '';

    // Autocomplete
    if (col.type === 'autocomplete') {
      return (
        <AutoCompleteInput
          col={col}
          value={value}
          rowIndex={rowIndex}
          colIndex={colIndex}
          onChange={(id, name) => {
            handleEdit({ target: { value: id } }, rowIndex, col, row, colIndex);
            // row[col.columnName] = name;
          }}
        />
      );
    }

    // Dropdown
    if (col.isDropdown) {
      //  console.log('col:', col);
      const options =
        row[`${col.columnName}~options`] || col.dropdownOptions || [];

      // if group dropdown (has type)
      if (col.type === 'group-dropdown') {
        const groups = {};

        options.forEach((opt) => {
          const g = opt.group || 'Others';
          if (!groups[g]) groups[g] = [];
          groups[g].push(opt);
        });

        return (
          <select
            className="form-select form-select-sm"
            value={value}
            onChange={(e) => handleEdit(e, rowIndex, col, row, colIndex)}
          >
            <option value="">-- Select --</option>
            {Object.entries(groups).map(([grp, items]) => (
              <optgroup key={grp} label={grp}>
                {items.map((opt, i) => (
                  <option key={i} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        );
      }

      // normal dropdown
      return (
        <select
          className="form-select form-select-sm"
          value={value}
          onChange={(e) => handleEdit(e, rowIndex, col, row, colIndex)}
        >
          <option value="">-- Select --</option>
          {options.map((opt, i) => (
            <option key={i} value={opt.id}>
              {opt.name}
            </option>
          ))}
        </select>
      );
    }

    if (col.isCheckbox && Array.isArray(col.dropdownOptions)) {
      const selectedValues = Array.isArray(row[col.columnName])
        ? row[col.columnName]
        : [];
      // console.log('selected values: ', selectedValues);
      return (
        <div className="d-flex flex-row">
          {col.dropdownOptions.map((opt) => (
            <label key={opt.id} className="form-check-label me-3">
              <input
                type="checkbox"
                className="form-check-input me-1"
                value={opt.id}
                checked={selectedValues.map(String).includes(String(opt.id))}
                onChange={(e) => handleEdit(e, rowIndex, col, row, colIndex)}
              />
              {opt.name}
            </label>
          ))}
        </div>
      );
    }

    // Standard input
    if (col.type === 'input' && col.inputId) {
      if (col.inputId === 'file') {
        return (
          <input
            type="file"
            className="form-control form-control-sm"
            onChange={(e) => handleEdit(e, rowIndex, col, row, colIndex)}
          />
        );
      } else if (col.inputId === 'multi-file') {
        return (
          <input
            type="file"
            multiple
            className="form-control form-control-sm"
            onChange={(e) => handleEdit(e, rowIndex, col, row, colIndex)}
          />
        );
      } else {
        return (
          <input
            type={col.inputId}
            className="form-control form-control-sm"
            value={value || ''}
            onChange={(e) => handleEdit(e, rowIndex, col, row, colIndex)}
          />
        );
      }
    }

    // Editable content
    if (col.editable) {
      return (
        <div
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => handleEdit(e, rowIndex, col, row, colIndex)}
        >
          {value}
        </div>
      );
    }

    // Default: just show value
    return value;
  };

  // console.log(
  //   'data length: ',
  //   data?.length,
  //   ' filtered length: ',
  //   filteredData.length
  // );

  // console.log('filtered data:', filteredData);

  const exportToExcel = (data, title) => {
    // Step 1: Map data to have only first part of keys
    const mappedData = data.map((row) => {
      const newRow = {};
      for (const key in row) {
        const firstPart = key?.split('~')[0]; // get the first part before ~
        newRow[firstPart] = row[key];
      }
      return newRow;
    });

    // Step 2: Create sheet
    const ws = XLSX.utils.json_to_sheet(mappedData);

    // Step 3: Create workbook and export
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'data');
    XLSX.writeFile(wb, title + '.xlsx');
  };

  // ✅ Submit a single row: returns { success, message, row, rowIndex }
  // const submitRow = async (row, rowIndex) => {
  //   console.log('row2:', row);
  //   const filteredRow = filterRow(row);
  //   console.log('filteredRow2:', filteredRow);
  //   const route = filteredRow.api_route;
  //   delete filteredRow.api_route;

  //   const insertUrl = `${API_URL}/${ENDPOINTS.SYS}/create/${route}`;

  //   try {
  //     const response = await axios.post(insertUrl, filteredRow, {
  //       headers: {
  //         Authorization: token ? `Bearer ${token}` : '',
  //       },
  //     });

  //     return {
  //       success: response.data.success === 1,
  //       message: response.data.message,
  //       result: response.data,

  //       row,
  //       rowIndex,
  //     };
  //   } catch (error) {
  //     return {
  //       success: false,
  //       message: error.message,
  //       row,
  //       rowIndex,
  //     };
  //   }
  // };

  const handleSubmitAll = async () => {
    const selectedRowIndexes = Array.from(selectedRows);

    if (selectedRowIndexes.length === 0) {
      notification.warning({ message: 'No rows selected to submit.' });
      return;
    }

    for (let rowIndex of selectedRows) {
      console.log('row ', rowIndex, ':', originalData[rowIndex]);
      //  await handleSubmitOld(originalData[rowIndex], rowIndex);
    }

    // ✅ Now resolve indexes to actual rows:
    const selectedRowsArray = selectedRowIndexes.map((rowIndex) => {
      //   console.log('rowIndex:', rowIndex);
      // console.log(
      //   'originalData[rowIndex]:',
      //   originalData[rowIndex],
      //   'filteredData[rowIndex]:',
      //   filteredData[rowIndex]
      // );
      return filteredData[rowIndex]; // or originalData[rowIndex] if needed
    });

    const requests = selectedRowsArray.map(
      (row, i) => submitRow(row, selectedRowIndexes[i], token) // keep original index
    );

    console.log('requests:', requests);

    const results = await Promise.all(requests);
    console.log('results:', results);
    let successCount = 0;
    let failedCount = 0;

    results.forEach((result) => {
      if (result.success) {
        successCount++;
        setMessage(result.result);
        setProcessedRows((prev) => [...prev, result.rowIndex]);

        const updatedData = originalData.filter(
          (dataRow) => JSON.stringify(dataRow) !== JSON.stringify(result.row)
        );
        setOriginalData(updatedData);

        const updatedFilteredData = filteredData.filter(
          (dataRow) => JSON.stringify(dataRow) !== JSON.stringify(result.row)
        );
        setFilteredData(updatedFilteredData);
      } else {
        failedCount++;
      }
    });

    if (inputRef.current) {
      inputRef.current.focus();
    }
    setSearch('');

    notification.info({
      message: `Submit All Result`,
      description: `Success: ${successCount}, Failed: ${failedCount}`,
    });
    if (successCount>=1) {
      const url = `/${message.print_route}/${message.insertId}`;
      window.open(url, "_blank");
    }
    if (failedCount === 0) {
      setFilteredData([]);
    }
  };

  // useEffect(() => {
  //   // Automatically select all rows when filteredData changes
  //   if (filteredData.length > 0) {
  //     setSelectedRows(new Set(filteredData.map((_, i) => i)));
  //   }
  // }, [filteredData]);
  // console.log('mesage:', message);
  return (
    <div className=" ">
      <div className="row">
        <div className="col-9">
          {' '}
          {showApiUpdateButton && (
            <button
              onClick={updateApiValues}
              className="btn btn-danger"
              disabled={loading}
            >
              {loading ? '⏳ Updating...' : '🔄 Update API Values'}
            </button>
          )}
          <button
            onClick={() => exportToExcel(filteredData, `${link.page_title}`)}
            className="btn btn-primary"
          >
            Export Excel
          </button>
          {failedData && failedData.length > 0 && (
            <button
              onClick={() =>
                exportToExcel(failedData, `${link.page_title}-failed`)
              }
              className="btn btn-danger"
            >
              Export Errors {failedData?.length}
            </button>
          )}
        </div>
        <div className="col-3">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={handleSearch}
            className="form-control mb-3"
            style={{ maxWidth: '300px' }} // Optional: Limit max width for better alignment
            ref={inputRef} // Attach the ref here
          />
        </div>
      </div>
      <table className="table table-bordered">
        <thead className="table-dark">
          <tr>
            <th>
              <input
                type="checkbox"
                checked={selectedRows.size === filteredData.length}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedRows(new Set(filteredData.map((_, i) => i)));
                  } else {
                    setSelectedRows(new Set());
                  }
                }}
              />
            </th>

            {columns
              .filter((col) => col.visible && col.key !== 'color') // Ignore "color" column
              .map((col) => (
                <th key={col.key}>{col.header}</th>
              ))}
            {link.hide_tick === '0' && (
              <th>
                {selectedRows.size > 0 && (
                  <button
                    className="btn btn-primary mb-2"
                    onClick={handleSubmitAll}
                  >
                    Submit Selected Rows ({selectedRows.size})
                  </button>
                )}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {filteredData &&
            filteredData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={
                  row.color ? `row-color-${row.color.trim().toLowerCase()}` : ''
                }
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedRows.has(rowIndex)}
                    onChange={() => {
                      setSelectedRows((prev) => {
                        const updated = new Set(prev);
                        if (updated.has(rowIndex)) {
                          updated.delete(rowIndex);
                        } else {
                          updated.add(rowIndex);
                        }
                        return updated;
                      });
                    }}
                  />
                </td>
                {columns
                  .filter((col) => col.visible && col.key !== 'color')
                  .map((col, colIndex) => (
                    <td
                      key={col.key}
                      style={
                        col.editable ||
                        col.isDropdown ||
                        col.type === 'autocomplete'
                          ? { backgroundColor: '#f9f9f9' }
                          : {}
                      }
                    >
                      {renderCell({ col, row, rowIndex, colIndex, handleEdit })}
                    </td>
                  ))}
                {link.hide_tick === '0' && (
                  <td>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleSubmit(row, rowIndex)}
                    >
                      ✅
                    </button>
                  </td>
                )}
              </tr>
            ))}
        </tbody>
        <tfoot>
          <tr>
            <td></td> {/* Empty cell for the checkbox column */}
            {columns
              .filter((col) => col.visible && col.key !== 'color')
              .map((col, colIndex) => {
                if (col.sum) {
                  const total = filteredData.reduce((acc, row, rowIndex) => {
                    // check if rowIndex is in selectedRows
                    if (selectedRows.has(rowIndex)) {
                      return acc + (Number(row[col.columnName]) || 0);
                    }
                    return acc;
                  }, 0);

                  return (
                    <td key={col.key} style={{ fontWeight: 'bold' }}>
                      {total.toFixed(2)}
                    </td>
                  );
                }
                return <td key={col.key}></td>;
              })}
            {link.hide_tick === '0' && <td></td>}
          </tr>
        </tfoot>
      </table>

      <div className="text-end">
        {message && message?.insertId && message?.print_route && (
          <a
            href={`/${message?.print_route}/${message?.insertId}`}
            target="_blank"
            rel="noreferrer"
            className="btn btn-danger"
          >
            &nbsp;Print
          </a>
        )}
        <button className="btn btn-success" onClick={handleSubmitAll}>
          Save Selected - {selectedRows.size} records
        </button>
      </div>

      {executionTime !== null && (
        <p>
          Request executed in: {executionTime.toFixed(2)} sec - Total rows:
          {filteredData && filteredData.length} rows
        </p>
      )}
    </div>
  );
};

export default DataEntryTable;
